/**
 * Tasks (slice 2b spec §10.1; Paper F3, board 36): everything the crew is
 * working on. Tabs with counts (Open, Waiting on someone, Done, Canceled, in
 * the URL as `?filter=`), a table on a desktop and a list on a phone. A row
 * opens Task detail; New task opens its window.
 */
import { Button } from "@crew/ui/components/button";
import { AgentChip, PersonChip } from "@crew/ui/components/crew-avatar";
import { Fab } from "@crew/ui/components/fab";
import { Link } from "@crew/ui/components/link";
import { Spinner } from "@crew/ui/components/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@crew/ui/components/table";
import { Tabs, TabsList, TabsTrigger } from "@crew/ui/components/tabs";
import { PageHeader } from "@crew/ui/components/typography";
import { Plus } from "lucide-react";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { errorHint } from "../../app/api";
import type { Me } from "../../app/me";
import { agentTint, personTint } from "../../app/tints";
import { shortTime } from "../chats/format";
import { TASK_FILTERS, useTasks, type TaskCounts, type TaskFilter, type TaskItem } from "./api";
import { NewTaskWindow } from "./new-task";
import { todayIn } from "../areas/brief";
import { TaskStatus } from "./parts";
import { EMPTY_NOTES, TAB_LABELS } from "./task-view";
import { AreaFilter } from "../areas/area-controls";
import { useAreaFilter } from "../areas/use-area-filter";
import { filingArea } from "../areas/area-view";
import { AreaChip } from "../areas/parts";

const filterOf = (v: string | null): TaskFilter =>
  TASK_FILTERS.includes(v as TaskFilter) ? (v as TaskFilter) : "open";

/** "You" for the signed-in person, else their first name. */
function forName(task: TaskItem, me: Me): string | null {
  if (!task.for) return null;
  return task.for.id === me.user.id ? "You" : (task.for.name.split(/\s+/)[0] ?? task.for.name);
}

function FilterTabs({
  filter,
  counts,
  onChange,
}: {
  filter: TaskFilter;
  counts: TaskCounts | undefined;
  onChange: (f: TaskFilter) => void;
}) {
  return (
    <Tabs value={filter} onValueChange={(v) => onChange(v as TaskFilter)}>
      {/* On a phone the tabs scroll sideways rather than wrap. */}
      <div className="-mx-4 overflow-x-auto px-4 [scrollbar-width:none] sm:mx-0 sm:px-0">
        <TabsList aria-label="Filter tasks">
          {TASK_FILTERS.map((f) => (
            <TabsTrigger key={f} value={f} data-filter={f}>
              {TAB_LABELS[f]}
              <span className="text-muted-foreground tabular">{counts?.[f] ?? ""}</span>
            </TabsTrigger>
          ))}
        </TabsList>
      </div>
    </Tabs>
  );
}

function TaskTable({ tasks, me }: { tasks: TaskItem[]; me: Me }) {
  return (
    // The table's own scroll wrapper would still take a gap on a phone: hide the whole.
    <div className="hidden md:block" data-tasks-table>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Task</TableHead>
            <TableHead className="w-[200px]">Status</TableHead>
            <TableHead className="w-[170px]">Agent</TableHead>
            <TableHead className="w-[110px]">For</TableHead>
            <TableHead className="w-[90px] text-right">Updated</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {tasks.map((task) => {
            const who = forName(task, me);
            return (
              <TableRow key={task.id} data-task-id={task.id} className="relative">
                <TableCell className="max-w-0 whitespace-normal">
                  <Link
                    href={`/tasks/${task.id}`}
                    className="flex flex-col gap-0.5 after:absolute after:inset-0"
                  >
                    <span className="font-medium text-foreground">
                      {task.title}
                      <AreaChip area={task.area} className="ml-2" />
                    </span>
                    {task.detail ? (
                      <span className="truncate text-muted-foreground">{task.detail}</span>
                    ) : null}
                  </Link>
                </TableCell>
                <TableCell>
                  <TaskStatus
                    task={task}
                    viewerId={me.user.id}
                    today={todayIn(me.workspace.timezone)}
                  />
                </TableCell>
                <TableCell>
                  <AgentChip
                    name={task.agent.name}
                    glyph={task.agent.glyph}
                    tint={agentTint(task.agent.tint)}
                  />
                </TableCell>
                <TableCell>
                  {task.for && who ? (
                    <PersonChip
                      name={who}
                      initials={task.for.initials}
                      tint={personTint(task.for.tint)}
                    />
                  ) : null}
                </TableCell>
                <TableCell className="text-right text-muted-foreground">
                  {shortTime(task.updated_at, me.workspace.timezone)}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

/** The phone's list: title, status, updated. */
function TaskList({ tasks, me }: { tasks: TaskItem[]; me: Me }) {
  return (
    <ul className="flex flex-col divide-y divide-border md:hidden" data-tasks-list>
      {tasks.map((task) => (
        <li key={task.id} data-task-id={task.id}>
          <Link href={`/tasks/${task.id}`} className="flex flex-col gap-1.5 py-3.5">
            <span className="text-body font-medium text-foreground">
              {task.title}
              <AreaChip area={task.area} className="ml-2" />
            </span>
            <span className="flex items-center justify-between gap-3">
              <TaskStatus
                task={task}
                viewerId={me.user.id}
                today={todayIn(me.workspace.timezone)}
                className="min-w-0"
              />
              <span className="shrink-0 text-meta text-muted-foreground">
                {shortTime(task.updated_at, me.workspace.timezone)}
              </span>
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

function TasksBody({ filter, area, me }: { filter: TaskFilter; area: string | null; me: Me }) {
  const tasks = useTasks(filter, area);
  // Only when nothing loaded: a failed Older tasks keeps the rows it has (Devin on #260).
  if (tasks.error && !tasks.data) {
    return <p className="text-body text-muted-foreground">{errorHint(tasks.error)}</p>;
  }
  if (!tasks.data) {
    return (
      <div className="flex justify-center py-10 text-muted-foreground">
        <Spinner className="size-5" />
      </div>
    );
  }
  const items = tasks.data.pages.flatMap((p) => p.items);
  // A failed refresh keeps the rows it had: say they may be out of date (Devin on #260).
  const stale = tasks.isRefetchError ? (
    <p role="alert" data-tasks-stale className="text-meta text-brick">
      Couldn't refresh the tasks: these may be out of date. {errorHint(tasks.error)}
    </p>
  ) : null;
  if (items.length === 0) {
    return (
      <>
        {stale}
        <p
          data-tasks-empty={filter}
          className="rounded-xl border border-dashed border-border px-6 py-10 text-center text-body text-muted-foreground"
        >
          {EMPTY_NOTES[filter]}
        </p>
      </>
    );
  }
  return (
    <>
      {stale}
      <TaskTable tasks={items} me={me} />
      <TaskList tasks={items} me={me} />
      {tasks.isFetchNextPageError ? (
        <p role="alert" className="self-center text-meta text-brick">
          {errorHint(tasks.error)}
        </p>
      ) : null}
      {tasks.hasNextPage ? (
        <Button
          variant="ghost"
          className="self-center"
          disabled={tasks.isFetchingNextPage}
          onClick={() => void tasks.fetchNextPage()}
        >
          Older tasks
        </Button>
      ) : null}
    </>
  );
}

export function TasksPage({ me }: { me: Me }) {
  const [params, setParams] = useSearchParams();
  const filter = filterOf(params.get("filter"));
  const [area, setArea] = useAreaFilter();
  const counts = useTasks(filter, area).data?.pages[0]?.counts;
  const [creating, setCreating] = useState(false);
  const setFilter = (f: TaskFilter) =>
    setParams(
      (p) => {
        const next = new URLSearchParams(p);
        if (f === "open") next.delete("filter");
        else next.set("filter", f);
        return next;
      },
      { replace: true },
    );
  return (
    <main className="flex w-full max-w-6xl flex-col gap-5 px-4 pt-6 pb-16 sm:px-6 md:gap-6 md:px-12 md:pt-9">
      <PageHeader
        title="Tasks"
        description="Everything the crew is working on."
        actions={
          <Button
            onClick={() => setCreating(true)}
            data-new-task-button
            className="hidden md:inline-flex"
          >
            <Plus />
            New task
          </Button>
        }
      />
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between md:gap-4">
        <FilterTabs filter={filter} counts={counts} onChange={setFilter} />
        <AreaFilter me={me} value={area} onChange={setArea} />
      </div>
      <TasksBody filter={filter} area={area} me={me} />
      <Fab label="New task" onClick={() => setCreating(true)} data-new-task-fab>
        <Plus />
      </Fab>
      {/* Slice 6d (spec §4.2): New task on the list filtered to an Area makes it there. */}
      <NewTaskWindow open={creating} onOpenChange={setCreating} areaId={filingArea(area, me)} />
    </main>
  );
}
